import { createApi } from "@reduxjs/toolkit/query/react"
import { gql, ClientError } from "graphql-request"

const storefrontGraphqlApiUrl = `${
  import.meta.env.VITE_MYSHOPIFY_BASE_URL
}/api/${import.meta.env.VITE_SHOPIFY_STOREFRONT_API_VERSION}/graphql.json`

const requestHeaders = {
  "X-Shopify-Storefront-Access-Token": import.meta.env
    .VITE_SHOPIFY_STOREFRONT_PUBLIC_ACCESS_TOKEN,
  "X-GraphQL-Cost-Include-Fields": true,
}

const graphqlBaseQuery = ({ baseUrl }) => async ({ body, variables = {} }) => {
  try {
    // Use fetch directly for better control
    const response = await fetch(baseUrl, {
      method: 'POST',
      headers: {
        ...requestHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: body,
        variables
      })
    });

    // Handle different response types
    const contentType = response.headers.get('Content-Type');
    
    if (contentType && contentType.includes('multipart/mixed')) {
      // For multipart responses, let's extract the useful data
      const text = await response.text();
      let result = {};
      
      // Simple parser for multipart responses
      const parts = text.split('--graphql').filter(part => part.includes('Content-Type: application/json'));
      
      for (const part of parts) {
        try {
          const jsonStart = part.indexOf('{');
          if (jsonStart > -1) {
            const jsonString = part.substring(jsonStart);
            const endBrace = jsonString.lastIndexOf('}') + 1;
            const cleanJson = jsonString.substring(0, endBrace);
            const data = JSON.parse(cleanJson);
            
            // Merge data from each part
            if (data.data) {
              result = { ...result, ...data.data };
            } else if (data.incremental) {
              // Handle incremental updates if needed
              // (simplified - you might need more complex merging logic)
              data.incremental.forEach(update => {
                if (update.data && update.path) {
                  // Very simple merge approach - you might need something more sophisticated
                  if (update.path[0] === 'cart') {
                    if (!result.cart) result.cart = {};
                    Object.assign(result.cart, update.data);
                  }
                }
              });
            }
          }
        } catch (e) {
          console.error('Error parsing multipart response chunk', e);
        }
      }
      
      // If we have data, return it as a success
      if (Object.keys(result).length > 0) {
        return { data: result };
      }
    } else {
      // Regular JSON response
      const data = await response.json();
      return data;
    }
    
    // If we reach here, something went wrong
    return { error: { status: response.status, data: { message: 'Failed to parse response' } } };
    
  } catch (error) {
    // Create a serializable error
    return { 
      error: { 
        status: error.response?.status || 500,
        data: { 
          message: error.message || 'Unknown error',
          graphqlErrors: error.response?.errors || []
        }
      } 
    };
  }
};

export const CUSTOMIZABLE_LINE_ITEMS_FRAGMENT = gql`
  fragment ComponentizableCartLineFragment on ComponentizableCartLine {
    id
    merchandise {
      ... on ProductVariant {
        id
        title
        image {
          id
          src: url
          altText
          width
          height
        }
        product {
          id
          handle
          title
          productType
          metafield(namespace: "custom", key: "order_limits") {
            id
            namespace
            key
            value
          }
        }
        weight
        available: availableForSale
        sku
        selectedOptions {
          name
          value
        }
        compareAtPrice {
          amount
          currencyCode
        }
        price {
          amount
          currencyCode
        }
        unitPrice {
          amount
          currencyCode
        }
        unitPriceMeasurement {
          measuredType
          quantityUnit
          quantityValue
          referenceUnit
          referenceValue
        }
      }
    }
    quantity
    attributes {
      key
      value
    }
    cost {
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      amountPerQuantity {
        amount
        currencyCode
      }
      compareAtAmountPerQuantity {
        amount
        currencyCode
      }
    }
    discountAllocations {
      discountedAmount {
        amount
        currencyCode
      }
      discountApplication {
        targetType
        allocationMethod
        targetSelection
        value {
          ... on PricingPercentageValue {
            percentage
          }
          ... on MoneyV2 {
            amount
            currencyCode
          }
        }
      }
      ... on CartCodeDiscountAllocation {
        code
      }
      ... on CartAutomaticDiscountAllocation {
        title
      }
      ... on CartCustomDiscountAllocation {
        title
      }
    }
  }
`

export const LINE_ITEMS_FRAGMENT = gql`
  fragment CartLineFragment on CartLine {
    id
    merchandise {
      ... on ProductVariant {
        id
        title
        image {
          id
          src: url
          altText
          width
          height
        }
        product {
          id
          handle
          title
          productType
          metafield(namespace: "custom", key: "order_limits") {
            id
            namespace
            key
            value
          }
        }
        weight
        available: availableForSale
        sku
        selectedOptions {
          name
          value
        }
        compareAtPrice {
          amount
          currencyCode
        }
        price {
          amount
          currencyCode
        }
        unitPrice {
          amount
          currencyCode
        }
        unitPriceMeasurement {
          measuredType
          quantityUnit
          quantityValue
          referenceUnit
          referenceValue
        }
      }
    }
    quantity
    attributes {
      key
      value
    }
    cost {
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      amountPerQuantity {
        amount
        currencyCode
      }
      compareAtAmountPerQuantity {
        amount
        currencyCode
      }
    }
    discountAllocations {
      discountedAmount {
        amount
        currencyCode
      }
      discountApplication {
        targetType
        allocationMethod
        targetSelection
        value {
          ... on PricingPercentageValue {
            percentage
          }
          ... on MoneyV2 {
            amount
            currencyCode
          }
        }
      }
      ... on CartCodeDiscountAllocation {
        code
      }
      ... on CartAutomaticDiscountAllocation {
        title
      }
      ... on CartCustomDiscountAllocation {
        title
      }
    }
  }
`

export const DELIVERY_GROUPS_FRAGMENT = gql`
  fragment DeliveryGroups on Cart {
    deliveryGroups(first: 10, withCarrierRates: true) {
      edges {
        node {
          id
          deliveryOptions {
            title
            handle
            estimatedCost {
              amount
              currencyCode
            }
          }
          selectedDeliveryOption {
            title
            handle
            estimatedCost {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`

export const CartFragment = gql`
  ${LINE_ITEMS_FRAGMENT}
  ${CUSTOMIZABLE_LINE_ITEMS_FRAGMENT}
  ${DELIVERY_GROUPS_FRAGMENT}
  fragment CartFragment on Cart {
    id
    createdAt
    updatedAt
    lines(first: 10) {
      edges {
        node {
          ... on CartLine {
            ...CartLineFragment
          }
          ... on ComponentizableCartLine {
            ...ComponentizableCartLineFragment
          }
        }
      }
    }
    attributes {
      key
      value
    }
    cost {
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      totalTaxAmount {
        amount
        currencyCode
      }
      totalDutyAmount {
        amount
        currencyCode
      }
    }
    checkoutUrl
    discountCodes {
      applicable
      code
    }
    appliedGiftCards {
      id
      lastCharacters
      amountUsed {
        amount
      }
    }
    buyerIdentity {
      countryCode
      email
      phone
      customer {
        email
      }
      deliveryAddressPreferences {
        ... on MailingAddress {
          company
          firstName
          lastName
          address1
          address2
          city
          province
          country
          zip
          phone
        }
      }
    }
    delivery {
      addresses(selected: true) {
        address {
          ... on CartDeliveryAddress {
            company
            name
            firstName
            lastName
            address1
            address2
            city
            provinceCode
            countryCode
            zip
            phone
          }
        }
      }
    }
    ... DeliveryGroups @defer
    note
  }
`

const storefrontCartApi = createApi({
  reducerPath: "storefrontCartApi",
  baseQuery: graphqlBaseQuery({
    baseUrl: storefrontGraphqlApiUrl,
  }),
  endpoints: (builder) => ({
    getCart: builder.query({
      query: (cartId) => ({
        body: gql`
          ${CartFragment}
          query getCart($cartId: ID!) {
            cart(id: $cartId) {
              ...CartFragment
            }
          }
        `,
        variables: {
          cartId,
        },
      }),
      providesTags: ["Cart"],
    }),
    cartCreate: builder.mutation({
      query: (cartCreateInput = {}) => ({
        body: gql`
          ${CartFragment}
          mutation ($input: CartInput!) {
            cartCreate(input: $input) {
              userErrors {
                code
                field
                message
              }
              cart {
                ...CartFragment
              }
            }
          }
        `,
        variables: {
          input: cartCreateInput,
        },
      }),
      invalidatesTags: ["Cart"],
    }),
    cartLinesUpdate: builder.mutation({
      query: ({cartId, lines}) => ({
        body: gql`
          ${CartFragment}
          mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
                code
              }
            }
          }
        `,
        variables: {
          cartId,
          lines,
        },
      }),
    }),
    cartLinesAdd: builder.mutation({
      query: ({cartId, lines}) => ({
        body: gql`
          ${CartFragment}
          mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
                code
              }
              warnings {
                code
                message
                target
              }
            }
          }
        `,
        variables: {
          cartId,
          lines,
        },
      }),
    }),
    cartLinesRemove: builder.mutation({
      query: ({cartId, lineIds}) => ({
        body: gql`
          ${CartFragment}
          mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
                code
              }
            }
          }
        `,
        variables: {
          cartId,
          lineIds,
        },
      }),
    }),
    cartBuyerIdentityUpdate: builder.mutation({
      query: ({ cartId, buyerIdentity }) => ({
        body: gql`
          ${CartFragment}
          mutation cartBuyerIdentityUpdate(
            $buyerIdentity: CartBuyerIdentityInput!
            $cartId: ID!
          ) {
            cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
                code
              }
            }
          }
        `,
        variables: {
          cartId,
          buyerIdentity,
        },
      }),
    }),
    cartSelectedDeliveryOptionsUpdate: builder.mutation({
      query: ({ cartId, selectedDeliveryOptions }) => ({
        body: gql`
          ${CartFragment}
          mutation cartSelectedDeliveryOptionsUpdate(
            $cartId: ID!
            $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!
          ) {
            cartSelectedDeliveryOptionsUpdate(
              cartId: $cartId
              selectedDeliveryOptions: $selectedDeliveryOptions
            ) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
                code
              }
            }
          }
        `,
        variables: {
          cartId,
          selectedDeliveryOptions,
        },
      }),
    }),
    cartGiftCardCodesUpdate: builder.mutation({
      query: ({cartId, giftCardCodes}) => ({
        body: gql`
          ${CartFragment}
          mutation cartGiftCardCodesUpdate($cartId: ID!, $giftCardCodes: [String!]!) {
            cartGiftCardCodesUpdate(cartId: $cartId, giftCardCodes: $giftCardCodes) {
              cart {
                ...CartFragment
              }
              userErrors {
                code
                field
                message
              }
              warnings {
                code
                message
                target
              }
            }
          }
        `,
        variables: {
          cartId,
          giftCardCodes,
        },
      }),
      invalidatesTags: ["Cart"],
    }),
    cartGiftCardCodesRemove: builder.mutation({
      query: ({cartId, appliedGiftCardIds}) => ({
        body: gql`
          ${CartFragment}
          mutation cartGiftCardCodesRemove($appliedGiftCardIds: [ID!]!, $cartId: ID!) {
            cartGiftCardCodesRemove(appliedGiftCardIds: $appliedGiftCardIds, cartId: $cartId) {
              cart {
                ...CartFragment
              }
              userErrors {
                code
                field
                message
              }
              warnings {
                code
                message
                target
              }
            }
          }
        `,
        variables: {
          cartId,
          appliedGiftCardIds,
        },
      }),
      invalidatesTags: ["Cart"],
    }),
    cartDiscountCodesUpdate: builder.mutation({
      query: ({cartId, discountCodes}) => ({
        body: gql`
          ${CartFragment}
          mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
            cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
              cart {
                ...CartFragment
              }
              userErrors {
                code
                field
                message
              }
              warnings {
                code
                message
                target
              }
            }
          }
        `,
        variables: {
          cartId,
          discountCodes,
        },
      }),
      invalidatesTags: ["Cart"],
    }),
    cartNoteUpdate: builder.mutation({
      query: ({ cartId, note }) => ({
        body: gql`
          ${CartFragment}
          mutation cartNoteUpdate($cartId: ID!, $note: String!) {
            cartNoteUpdate(cartId: $cartId, note: $note) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        variables: {
          cartId,
          note,
        },
      }),
    }),
    cartAttributesUpdate: builder.mutation({
      query: ({ cartId, attributes }) => ({
        body: gql`
          ${CartFragment}
          mutation cartAttributesUpdate($attributes: [AttributeInput!]!, $cartId: ID!) {
            cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
              cart {
                ...CartFragment
              }
              userErrors {
                field
                message
                code
              }
            }
          }
        `,
        variables: {
          cartId,
          attributes,
        },
      }),
    }),
    cartGetAvailableShippingRates: builder.query({
      query: (cartId) => ({
        body: gql`
          ${DELIVERY_GROUPS_FRAGMENT}
          query cartGetAvailableShippingRates($cartId: ID!) {
            cart(id: $cartId) {
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalAmount {
                  amount
                  currencyCode
                }
              }
              ... DeliveryGroups @defer
            }
          }
        `,
        variables: {
          cartId,
        },
      }),
      // transformResponse: (response) => ({ cart: response.node }),
      providesTags: ["AvailableShippingRates"],
    }),
  }),
});

export const {
  useLazyGetCartQuery,
  useCartCreateMutation,
  useCartLinesUpdateMutation,
  useCartLinesAddMutation,
  useCartLinesRemoveMutation,
  useCartBuyerIdentityUpdateMutation,
  useCartGiftCardCodesUpdateMutation,
  useCartGiftCardCodesRemoveMutation,
  useCartDiscountCodesUpdateMutation,
  useCartSelectedDeliveryOptionsUpdateMutation,
  useCartNoteUpdateMutation,
  useCartAttributesUpdateMutation,
  useLazyCartGetAvailableShippingRatesQuery,
} = storefrontCartApi

export default storefrontCartApi
